import React, { useCallback, useMemo, useRef } from 'react';
import { useAppPrintMode } from '../../../print';
import { formatExportableSubjectWithOptionalFee, formatSubjectWithOptionalFee } from '../../../legend';
import { StudioHighchart } from '../../../highchart/Highchart';
import DownloadableContentBlock from '../../../content-block/DownloadableContentBlock';
import { useBlockContext } from '../../contexts/block-context';
import type HighchartsReact from 'highcharts-react-official';
import { useRecoilValue } from 'recoil';
import {
  blockExportMetadata,
  blockFonts,
  blockSelectedCustomizableMetrics,
  blockSubjectHasProxyState,
  customizedBlock,
} from 'venn-state';
import { useTheme } from 'styled-components';
import { useFilteredRequests } from '../../utils/hooks/use-filtered-requests';
import { useSubjectColors } from '../../../studio-blocks/logic/useSubjectColors';
import { returnsDistributionParser as makeReturnsDistributionRowData } from '../../../studio-blocks/logic/parsers';
import { merge } from 'lodash';
import {
  convertReturnDataToExcel,
  getDefaultChartConfig,
  getOptions,
} from '../../../studio-blocks/components/charts/returns-distribution/utils';
import { WATERMARK_POSITION_TOP } from '../../../studio-blocks/customAnalysisContants';
import useExportUpdate from '../../../studio-blocks/logic/useExportUpdate';
import { getAnalyzedSubjectFromRequestSubject } from 'venn-utils';
import { usePageProxySymbol } from '../../components/core/block-subject-footnotes-and-superscripts';

export const DistributionContent = ({ id }: { id: string }) => {
  const subjectHasProxy = useRecoilValue(blockSubjectHasProxyState(id));
  const legendFont = useRecoilValue(blockFonts.blockChartLegend(id));
  const axisFont = useRecoilValue(blockFonts.blockChartAxis(id));
  const customizableMetrics = useRecoilValue(blockSelectedCustomizableMetrics(id));
  const block = useRecoilValue(customizedBlock(id));
  const exportMetaData = useRecoilValue(blockExportMetadata(id));
  const proxySymbol = usePageProxySymbol();

  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const theme = useTheme();
  const { analyses, requests } = useFilteredRequests(id);
  const { inPrintModeOrReportLab } = useAppPrintMode();
  const { blockRef } = useBlockContext();

  const analyzedSubjects = useMemo(
    () => requests.map(({ subject }) => subject).map(getAnalyzedSubjectFromRequestSubject),
    [requests],
  );
  const subjectColors = useSubjectColors(analyzedSubjects);
  const rowData = makeReturnsDistributionRowData(customizableMetrics)(block, analyses);

  const defaultHeader =
    block?.relativeToBenchmark && customizableMetrics?.[0]?.relativeLabel
      ? customizableMetrics[0].relativeLabel
      : (customizableMetrics?.[0]?.label ?? 'Builder Chart');

  const excelDataFn = useCallback(() => {
    const subjectNames = requests.map(({ subject }) => formatExportableSubjectWithOptionalFee(subject));
    const relatives = requests.map(({ relative }) => relative);
    return (
      convertReturnDataToExcel(
        rowData.filter(({ exportable }) => exportable),
        subjectNames,
        relatives,
      ) || []
    );
  }, [rowData, requests]);

  useExportUpdate({ selectedRefId: id, exportMetaData, excelDataFn });

  const options = useMemo(
    () =>
      rowData?.length
        ? merge(
            {},
            getDefaultChartConfig(theme, Boolean(inPrintModeOrReportLab)),
            getOptions({
              customBlockType: 'DISTRIBUTION' as const,
              print: inPrintModeOrReportLab,
              percent: rowData[0]?.percent,
              yAxisLabel: rowData[0]?.yAxisLabel ?? '',
              yAxisTitle: rowData[0]?.yAxisTitle,
              xAxisCategories: rowData[0]?.xAxisCategories,
              series: requests.map((request, index) => ({
                type: 'column' as const,
                name: formatSubjectWithOptionalFee(request.subject, proxySymbol, !!subjectHasProxy[request.subject.id]),
                color: subjectColors[index],
                data: [...(rowData?.[index]?.seriesData || [])],
              })),
              typography: theme.Typography,
              legendFontSize: legendFont.fontSizePt,
              axisFontSize: axisFont.fontSizePt,
            }),
          )
        : undefined,
    [
      rowData,
      theme,
      inPrintModeOrReportLab,
      requests,
      legendFont.fontSizePt,
      axisFont.fontSizePt,
      proxySymbol,
      subjectHasProxy,
      subjectColors,
    ],
  );

  return (
    <DownloadableContentBlock
      header={null}
      noBorder
      floatingOptions
      downloadable={{
        png: true,
        options: {
          fileName: block.header ?? defaultHeader,
          watermark: { top: WATERMARK_POSITION_TOP, right: 20 },
        },
        tracking: {
          description: 'BUILDER',
          relativeToBenchmark: block.relativeToBenchmark,
          userUploaded: false,
          subjectType: undefined,
          subjectId: undefined,
        },
        disabled: rowData ? rowData.length === 0 : true,
        target: blockRef,
      }}
    >
      {options && <StudioHighchart options={options} blockId={id} chartRef={chartRef} style={{ height: '100%' }} />}
    </DownloadableContentBlock>
  );
};
